<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h2>
          <v-icon class="mr-2">
            mdi-chart-timeline-variant-shimmer
          </v-icon>
          行政報告換算係数
        </h2>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12" md="6">
        <v-btn-toggle
          v-model="conditions.type"
          class="toggleButton"
          color="info accent-3"
          tile
          light
          mandatory
          rounded
          @change="menuChangeEvent"
        >
          <v-btn
            value="industrial"
            width="150"
          >
            産業廃棄物
          </v-btn>
          <v-btn
            value="specific_industrial"
            width="150"
          >
            特定産業廃棄物<br />
            （放射性物質）
          </v-btn>
          <v-btn
            value="general"
            width="150"
          >
            一般廃棄物
          </v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col>
        <div class="text-right">
          <h4>換算係数1.5の場合、1立方メートル=1.5トン、1リットル=0.0015トンとなります。</h4>
        </div>
      </v-col>
    </v-row>

    <!-- ページャ -->
    <v-row>
      <v-col>
        <custom-pagination
          :page-number="page"
          :per-page="perPage"
          :pagination="pagination"
          :total-visible="totalVisible"
          @pageChanged="changePage"
          @perPageChanged="changePerPage"
        />
      </v-col>
    </v-row> 
    <!-- /ページャ -->

    <v-row>
      <v-col>
        <validation-observer
          ref="ConversionFactorDetailsValidation"
        >
          <v-form>
            <v-card class="rounded-card" flat>
              <v-card-text>
                <v-data-table
                  :headers="tableHeaders"
                  :items="conversionFactorDetails"
                  :items-per-page="-1"
                  dense
                  disable-sort
                  hide-default-footer
                >

                  <template v-slot:[`item.cubic_meter_standard_factor`]="{ item }">
                    {{ item.cubic_meter_standard_factor | comma_format }}
                  </template>

                  <template v-slot:[`item.use_cubic_meter_factor`]="{ item }">
                    <v-checkbox
                      v-model="item.use_cubic_meter_factor"
                      dense
                    />
                  </template>

                  <template v-slot:[`item.cubic_meter_factor`]="{item}">
                    <validation-provider
                      v-slot="{ errors }"
                      :rules="{
                        required: !!item.use_cubic_meter_factor,
                        double: true,
                        min_value: !!item.use_cubic_meter_factor ? 0.001 : false,
                        max_value: !!item.use_cubic_meter_factor ? 999.999 : false,
                        quantityWithDecimal: !!item.use_cubic_meter_factor ? { int_digits: 3, dec_digits: 3 } : {}
                    }"
                      :vid="`cubic_meter_factor_${item.key}`"
                      name="個別 立方メートル"
                    >
                      <v-text-field
                        v-model="item.cubic_meter_factor"
                        :disabled="!(item.use_cubic_meter_factor)"
                        :error-messages="errors"
                        :filled="!(item.use_cubic_meter_factor)"
                        :show-size="10000"
                        dense
                        hide-details="auto"
                        outlined
                        persistent-placeholder
                        reverse
                        required
                        type="number"
                        style="font-size: small"
                        @keydown.69.prevent
                      />
                    </validation-provider>
                  </template>

                  <template v-slot:[`item.use_unit_factor`]="{ item }">
                    <v-checkbox
                      v-model="item.use_unit_factor"
                      dense
                    />
                  </template>

                  <template v-slot:[`item.unit_factor`]="{item}">
                    <validation-provider
                      v-slot="{ errors }"
                      :rules="{ required: !!item.use_unit_factor,
                        double: true,
                        min_value: !!item.use_unit_factor ? 0.001 : false,
                        max_value: !!item.use_unit_factor ? 999.999 : false,
                        quantityWithDecimal: !!item.use_unit_factor ? { int_digits: 3, dec_digits: 3 } : {}
                    }"
                      :vid="`unit_factor_${item.key}`"
                      name="個別 個・台係数"
                    >
                      <v-text-field
                        v-model="item.unit_factor"
                        :disabled="!(item.use_unit_factor)"
                        :error-messages="errors"
                        :filled="!(item.use_unit_factor)"
                        :show-size="10000"
                        dense
                        hide-details="auto"
                        outlined
                        persistent-placeholder
                        reverse
                        required
                        type="number"
                        style="font-size: small"
                        @keydown.69.prevent
                      />
                    </validation-provider>
                  </template>
                </v-data-table>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <VButtonProcessing
                  :disabled="conversionFactorDetails.length <= 0"
                  :onclick="registerButtonClickEvent"
                  color="primary"
                  elevation="5"
                >
                  登録
                </VButtonProcessing>
              </v-card-actions>
            </v-card>
          </v-form>
        </validation-observer>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <custom-pagination
          :page-number="page"
          :per-page="perPage"
          :pagination="pagination"
          :total-visible="totalVisible"
          @pageChanged="changePage"
          @perPageChanged="changePerPage"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>

import {mapActions, mapGetters, mapState} from 'vuex'
import VButtonProcessing from '../../../../components/VButtonProcessing'
import CustomPagination from "@/driver/components/_CustomPagination.vue";

export default {
  name: 'conversionFactorDetails',
  components: {
    CustomPagination,
    VButtonProcessing
  },
  mixins: [],
  props: {},
  data() {
    return {
      page: 1,
      perPage: 50,
      totalVisible: 7,
      conditions: {
        type: "industrial",
        limit: 50,
      },
      tableHeaders: [
        { value: 'code', text: 'コード', width: '6%' },
        { value: 'name', text: '分類名', width: '12%' },
        { value: 'parent_name', text: '廃棄物の種類', width: '12%' },
        { value: 'cubic_meter_standard_factor', text: '立方メートル・リットル\r\n標準係数', align: 'end', width: '10%'},
        { value: 'use_cubic_meter_factor', text: '個別\r\n利用'},
        { value: 'cubic_meter_factor', text: '立方メートル・リットル\r\n個別係数', align: 'end', width: '20%'},
        { value: 'unit_standard_factor', text: ' 個・台\r\n標準係数', align: 'end', width: '10%'},
        { value: 'use_unit_factor', text: '個別\r\n利用' },
        { value: 'unit_factor', text: ' 個・台\r\n個別係数', align: 'end', width: '20%'},
      ],
      conversionFactorDetails: [],
    }
  },
  computed: {
    ...mapState('DriverMasterConversionFactorDetail', {
      pagination: 'pagination',
    }),
    ...mapGetters('DriverMasterConversionFactorDetail', [
      'hasResult'
    ]),
  },
  created() {

    if (this.$route.params.resetCondition) {
      this.resetState()
    }

    if (this.$route.params.resetPagination) {
      this.resetPagination()
    }
    this.page = this.pagination.current_page
    this.searchButtonClickEvent()
  },

  methods: {
    ...mapActions('DriverMasterConversionFactorDetail', {
      fetchConversionFactorDetails: 'index',
      registerConversionFactorDetails: 'create',
      resetState: 'resetState'
    }),
    menuChangeEvent() {
      this.page = 1;
      this.searchButtonClickEvent();
    },
    /**
     * 検索ボタン クリックイベント
     */
    searchButtonClickEvent () {
      let params = { 
        condition: this.conditions,
        page: this.page,
      }
      this.fetchConversionFactorDetails(params).then((content) => {
        this.conversionFactorDetails = content.conversion_factor_details
      })
    },
    changePage(page) {
      this.page = page;
      this.searchButtonClickEvent()
    },
    changePerPage (perPage) {
      this.perPage = perPage
      this.page = 1
      this.searchButtonClickEvent()
    },
    /**
     * 登録ボタン クリックイベント
     * @return {Promise<unknown>}
     */
    registerButtonClickEvent () {
      return new Promise((resolve) => {
        setTimeout(() => {
          let $self = this
          this.$refs.ConversionFactorDetailsValidation.validateWithInfo().then(({ isValid, errors }) => {
            if(isValid) {
              $self.sendRegistrationInformation()
            } else {
              let messages = []
              for (let key in errors) {
                if (errors[key].length > 0) {
                  messages.push(errors[key][0])
                }
              }
            }
          })
          resolve();
        }, 1000);
      });
    },
    /**
     * 入力内容の送信
     */
    sendRegistrationInformation () {
      let details = []
      for (const conversionFactorDetail of this.conversionFactorDetails) {
        let detail = {
          conversion_factor_id: conversionFactorDetail.id,
          use_cubic_meter_factor: conversionFactorDetail.use_cubic_meter_factor,
          use_unit_factor: conversionFactorDetail.use_unit_factor,
        }
        if (conversionFactorDetail.use_cubic_meter_factor) {
          detail["cubic_meter_factor"] = conversionFactorDetail.cubic_meter_factor
        }
        if (conversionFactorDetail.use_unit_factor) {
          detail["unit_factor"] = conversionFactorDetail.unit_factor
        }
        details.push(detail)
      }

      const parameters = {
        conversion_factor_details: details,
      }
      this.registerConversionFactorDetails({ params: parameters }).then(() => {
        this.searchButtonClickEvent()
      })
    },
  }
}
</script>

<style scoped lang="scss">
.panel-header-color {
  color: #0D74BA !important;
  & .v-expansion-panel-header__icon {
    & .v-icon {
      color: #0D74BA !important;
      &::before {
        color: #0D74BA !important;
      }
    }
  }
}
.panel-selectable {
  background-color: #DCE7EB;
}
</style>

<style lang="scss">
.supplierBlock {
  & .v-input__append-inner {
    margin-top: 6px !important;
  }
}
</style>

<style lang="scss">
.supplierBlock {
  & .v-input__append-inner {
    margin-top: 6px !important;
  }
}
</style>

